// How the customer points at an item (CHT-746, contract `docs/architecture/milestone-d-contracts.md` 6.1): by its `stableId`, or by a
// place in the plan that they said in words ("the second activity", "dinner on day two"). The SERVER resolves the words against the
// current plan. When more than one item could be meant, the answer is AMBIGUOUS with the candidates, and the agent asks the customer:
// nothing is guessed. Pure and total.
export const referenceScopes = Object.freeze(["ACTIVITY", "MEAL", "ANY"]);
/** The largest ordinal and the most candidates an answer lists. */
export const REFERENCE_BOUNDS = Object.freeze({ maxOrdinal: 20, maxCandidates: 8 });
const isTransit = (item) => item.kind === "TRANSIT" || item.kind === "BUFFER";
const isMealItem = (item) => item.catalogType === "DINING";
function inScope(item, scope) {
    if (item.itemStatus === "CANCELLED" || isTransit(item))
        return false;
    switch (scope) {
        case "ACTIVITY":
            return !isMealItem(item);
        case "MEAL":
            return isMealItem(item);
        case "ANY":
            return true;
    }
}
/** Plan order inside a day: the start time where there is one (the order the customer reads), then the position. */
function timelineOrder(a, b) {
    const sectionOrder = a.sectionPosition - b.sectionPosition;
    if (sectionOrder !== 0)
        return sectionOrder;
    const start = (item) => item.startsAt === null ? Number.POSITIVE_INFINITY : item.startsAt.getTime();
    if (start(a) !== start(b))
        return start(a) < start(b) ? -1 : 1;
    return a.position - b.position;
}
/**
 * Resolves a reference against the current plan. A `stableId` resolves only when the plan holds it. An ordinal with a date counts inside
 * that day. An ordinal with no date resolves only when exactly one day has items in scope; with several days it is AMBIGUOUS, and the
 * candidates are the item at that place on each day that has one (so the customer can say "the one on day two").
 */
export function resolveItemReference(items, reference) {
    if ("stableId" in reference) {
        return items.some((item) => item.stableId === reference.stableId)
            ? { outcome: "RESOLVED", stableId: reference.stableId }
            : { outcome: "NOT_FOUND", itemsInScope: 0 };
    }
    const { position } = reference.ordinal;
    const scope = reference.ordinal.scope ?? "ACTIVITY";
    const date = reference.ordinal.date ?? null;
    if (!Number.isInteger(position) || position < 1 || position > REFERENCE_BOUNDS.maxOrdinal) {
        return { outcome: "NOT_FOUND", itemsInScope: 0 };
    }
    const ordered = items.filter((item) => inScope(item, scope)).sort(timelineOrder);
    const byDay = new Map();
    for (const item of ordered) {
        const list = byDay.get(item.localDate) ?? [];
        list.push(item);
        byDay.set(item.localDate, list);
    }
    if (date !== null) {
        const day = byDay.get(date) ?? [];
        const picked = day[position - 1];
        return picked === undefined
            ? { outcome: "NOT_FOUND", itemsInScope: day.length }
            : { outcome: "RESOLVED", stableId: picked.stableId };
    }
    const days = [...byDay.entries()];
    if (days.length === 0)
        return { outcome: "NOT_FOUND", itemsInScope: 0 };
    if (days.length === 1) {
        const [, only] = days[0];
        const picked = only[position - 1];
        return picked === undefined
            ? { outcome: "NOT_FOUND", itemsInScope: only.length }
            : { outcome: "RESOLVED", stableId: picked.stableId };
    }
    const candidates = [];
    for (const [day, list] of days) {
        const picked = list[position - 1];
        if (picked !== undefined) {
            candidates.push({
                stableId: picked.stableId,
                title: picked.title,
                localDate: day,
                ordinalInDay: position,
            });
        }
    }
    if (candidates.length === 0)
        return { outcome: "NOT_FOUND", itemsInScope: ordered.length };
    return {
        outcome: "AMBIGUOUS",
        candidates: candidates.slice(0, REFERENCE_BOUNDS.maxCandidates),
    };
}
